
import React, { useState } from 'react';
import api from '../api';
import { FormField } from '../types'
import ListItemView from './ListItemView';
import { list } from 'postcss';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import TextArea from "./field_types/RichText";


interface SchemaProps {
    docId: string | undefined;
    formFields: FormField[];
    updateSchema: any;
    generateSubSchema: any;
    path: string[];
    setNeedsUpdate: any;
    type?: string;
}

const SchemaEdit: React.FC<SchemaProps> = ({ docId, formFields, updateSchema, generateSubSchema, path, setNeedsUpdate, type }) => {
    const [addListItemModal, setAddListItemModal] = useState(false);
    const [listItem, setListItem] = useState({} as any);

    // New Field Mode
    const [newField, setNewField] = useState(false);
    const [newFieldName, setNewFieldName] = useState('');
    const [newFieldDescription, setNewFieldDescription] = useState('');
    const [newFieldType, setNewFieldType] = useState('');
    const [newFieldRequired, setNewFieldRequired] = useState(false);
    const [newFieldValues, setNewFieldValues] = useState('');

    //Edit Field Mode
    const [editFieldMode, setEditFieldMode] = useState({} as any);
    const [editFieldName, setEditFieldName] = useState('');
    const [editFieldDescription, setEditFieldDescription] = useState('');
    const [editFieldType, setEditFieldType] = useState('');
    const [editFieldRequired, setEditFieldRequired] = useState(false);
    const [editFieldValues, setEditFieldValues] = useState('');

    const isParent = path.length === 0;

    const addItem = (field: any, fieldPath: string[]) => {

        if (field && field.schema.length > 0) {
            //setAddListItemModal(true);
            setListItem((prevValue: any) => ({ ...prevValue, [field.name]: { show: true, mode: 'create' } }));
        }
    };

    const readItem = async (field: any, fieldPath: string[], row: any, rowIndex: any) => {

        if (field && field.schema.length > 0) {
            //setAddListItemModal(true);
            await updateSchema(row, fieldPath, field.name, 'readListItem')
            setListItem((prevValue: any) => ({ ...prevValue, [field.name]: { show: true, mode: 'edit', rowIndex } }));
        }

    };

    const cancelAddItem = (field: any) => {
        setListItem((prevValue: any) => ({ ...prevValue, [field.name]: { show: false, mode: null } }));
    };

    //console.log(path)
    const saveItem = async (field: any, fieldPath: string[]) => {

        // Save the item
        if (listItem[field.name]['mode'] === 'create') {
            await updateSchema(field.schema, fieldPath, field.name, 'createListItem')
        } else {
            await updateSchema(listItem[field.name]['rowIndex'], fieldPath, field.name, 'updateListItem')
        }

        // Clear the schema
        await updateSchema(field.schema, fieldPath, field.name, 'clearListSchema')
        // Close item create modal
        setNeedsUpdate(true);
        setListItem((prevValue: any) => ({ ...prevValue, [field.name]: { show: false, mode: null } }));


    };

    const removeSubSchema = async (field: any, fieldPath: string[]) => {

        await updateSchema(field.schema, fieldPath, field.name, 'removeSubSchema')
        setNeedsUpdate(true);

    };

    const removeListItem = async (field: any, fieldPath: string[], index: any) => {

        await updateSchema(index, fieldPath, field.name, 'removeListItem')
        setNeedsUpdate(true);
    }

    const onDragEnd = async (result: any) => {
        if (!result.destination) {
          return;
        }

        await updateSchema({ startIndex: result.source.index, endIndex: result.destination.index }, path, '', 'updateFieldOrder');
        setNeedsUpdate(true);

      };

    const addNewField = async (fieldPath: string[]) => {

        let options;

        if (newFieldValues  && newFieldType === 'dropdown_single' || newFieldType === 'dropdown_multiple') {
            options = newFieldValues.split(',').map((value: string) => value.trim());
        } else {
            options = null;
        }

        const newFieldData = {
            name: newFieldName,
            description: newFieldDescription,
            type: newFieldType,
            required: newFieldRequired,
            enum: options
        };

        await updateSchema(newFieldData, fieldPath, '', 'addField');
        setNewField(false);
        setNeedsUpdate(true);

        // clear new field data
        setNewFieldName('');
        setNewFieldDescription('');
        setNewFieldType('');
        setNewFieldRequired(false);
        setNewFieldValues('');
    };

    const editField = async (field: any, fieldPath: string[]) => {

        let options;

        // If the user has entered values for the dropdown or multiselect, split them into an array
        if (editFieldValues && (editFieldType === 'dropdown_single' || editFieldType === 'dropdown_multiple')) {
            options = editFieldValues.split(',').map((value: string) => value.trim());
        } else {
            options = null;
        }

        const isFieldTypeChanged = editFieldType !== field.type;


        const editedFieldData = {
            name: editFieldName,
            description: editFieldDescription,
            type: editFieldType,
            required: editFieldRequired,
            enum: options,
            value: isFieldTypeChanged ? null : field.value
        };

        await updateSchema(editedFieldData, fieldPath, field.name, 'editField');

        setEditFieldMode((prevValue: any) => ({ ...prevValue, [field.name]: null }));

        setNeedsUpdate(true);

    };

    const enableEditMode = async (field: any, fieldPath: string[]) => {

        setEditFieldName(field.name);
        setEditFieldDescription(field.description);
        setEditFieldType(field.type);
        setEditFieldRequired(field.required);
        setEditFieldValues(field.enum ? field.enum.join(', ') : null);


        const initEditFieldData = {
            name: field.name,
            description: field.description,
            type: field.type,
            required: field.required,
            editMode: true,
            ...(field.enum && { enum: field.enum })
        };
            
        setEditFieldMode((prevValue: any) => ({ ...prevValue, [field.name]: initEditFieldData }));
    };

    const deleteField = async (field: any, fieldPath: string[]) => {

        await updateSchema(field, fieldPath, field.name, 'deleteField');
        setNeedsUpdate(true);

    };

    return (
        <div className={`${isParent || type === 'object' ? '' : 'max-h-96 p-4'}`}>
        <DragDropContext onDragEnd={onDragEnd}>
         <Droppable droppableId="droppable">
         {(provided, snapshot) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
            {formFields.sort((a: FormField, b: FormField) => a.order - b.order).map((field: FormField, key) => (
            <Draggable key={field.name} draggableId={field.name} index={key}>
                {(provided, snapshot) => (

                <div ref={provided.innerRef} {...provided.draggableProps} className={`${key === 0 ? 'mt-4' : 'mt-10'} gap-x-6 gap-y-8 py-8 pl-2 pr-6 rounded-xl border group border-dashed`}>
                    <div className="flex flex-row">
                    <div {...provided.dragHandleProps}  className="text-slate-50 group-hover:text-slate-300 items-center grid pr-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                        </svg>

                    </div>

                    {field.type === 'short_text' && !editFieldMode[field.name] && (
                        <div className="flex-grow">
                            <label htmlFor={field.name} className="block text-sm font-medium leading-6 text-slate-900">
                                {field.name}
                            </label>
                            <p className="text-xs leading-6 text-slate-400">{field.description}</p>
                            <div className="mt-3">
                                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-slate-300">
                                    <input
                                        type="text"
                                        name={field.name}
                                        id={field.name}
                                        autoComplete={field.name}
                                        className="block flex-1 border-0 bg-transparent py-1.5 pl-2 text-slate-900 placeholder:text-slate-400 focus:ring-0 sm:text-sm sm:leading-6"
                                        placeholder=""
                                        value={field.value || ''}
                                        onChange={(e) => updateSchema(e.target.value, path, field.name, 'updateFormFieldValue')}
                                        required={field.required}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {field.type === 'textarea' && (
                        <div className="">
                        <label htmlFor={field.name} className="block text-sm font-medium leading-6 text-slate-900">
                            {field.name}
                        </label>
                        <p className="text-xs leading-6 text-slate-400">{field.description}</p>
                    </div>

                    )}
                    {field.enum && field.enum.length > 0 && !editFieldMode[field.name] && (
                        <div className="flex-grow">
                            <label htmlFor={field.name} className="block text-sm font-medium leading-6 text-slate-900">
                                {field.name}
                            </label>
                            <p className="text-xs leading-6 text-slate-400">{field.description}</p>
                            <div className="mt-3">
                                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-slate-300">
                                    <select
                                        name={field.name}
                                        id={field.name}
                                        autoComplete={field.name}
                                        className="block flex-1 border-0 bg-transparent py-1.5 pl-2 text-slate-900 placeholder:text-slate-400 focus:ring-0 sm:text-sm sm:leading-6"
                                        value={field.value || ''}
                                        onChange={(e) => updateSchema(e.target.value, path, field.name, 'updateFormFieldValue')}
                                        required={field.required}
                                        multiple={field.type === 'dropdown_multiple' ? true : false}
                                    >
                                        {field.enum.map((option: string) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                    {field.type === 'number' && !editFieldMode[field.name] && (
                        <div className="flex-grow">
                            <label htmlFor={field.name} className="block text-sm font-medium leading-6 text-slate-900">
                                {field.description}
                            </label>
                            <div className="mt-2">
                                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-slate-300">
                                    <input
                                        type="number"
                                        name={field.name}
                                        id={field.name}
                                        autoComplete='off'
                                        className="block flex-1 border-0 bg-transparent py-1.5 pl-2 text-slate-900 placeholder:text-slate-400 focus:ring-0 sm:text-sm sm:leading-6"
                                        placeholder=""
                                        value={field.value || ''}
                                        onChange={(e) => updateSchema(e.target.value, path, field.name, 'updateFormFieldValue')}
                                        required={field.required}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {field.type === 'array' && (
                    <div className="items-center justify-center">
                        {listItem[field.name] && listItem[field.name]['show'] && field.schema && field.schema.length > 0 && (
                        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-600 bg-opacity-50 backdrop-blur-sm">
                            <div className="bg-white p-4 px-8 rounded-lg shadow-lg w-full max-w-5xl mx-auto my-0">
                            
                            <div className="overflow-y-auto mt-4 scrollbar-thin">
                                <label htmlFor={field.name} className="block text-lg font-medium leading-6 text-slate-900">
                                {field.description}
                                </label>
                                <SchemaEdit
                                docId={docId}
                                formFields={field.schema}
                                updateSchema={updateSchema}
                                generateSubSchema={generateSubSchema}
                                path={[...path, field.name]}
                                setNeedsUpdate={setNeedsUpdate}
                                />
                            </div>

                            <div className="flex justify-between mt-4 p-4 sticky bottom-0 bg-white border-t">
                                <button
                                className="px-4 py-2 bg-slate-500 text-white rounded-md"
                                onClick={() => cancelAddItem(field)}
                                >
                                Cancel
                                </button>
                                {/* <button
                                className="px-4 py-2 bg-blue-500 text-white rounded-md"
                                onClick={() => saveItem(field, path)}
                                >
                                Save
                                </button> */}
                            </div>

                            </div>
                        </div>
                        )}
                            <label htmlFor={field.name} className="block text-sm font-medium leading-6 text-slate-900">
                                {field.description}
                            </label>
                            {field.schema ? (
                                <div>
                                <button onClick={() => addItem(field, [...path, field.name])} className="bg-white hover:bg-blue-200 text-slate-400 font-light text-xs py-2 px-2 rounded-xl border mt-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </button>
                                    <button onClick={(e) => removeSubSchema(field, path)} className="bg-white hover:bg-red-200 text-slate-400 font-light text-xs py-2 px-2 rounded-xl border ml-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m6 4.125l2.25 2.25m0 0l2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                                    </svg>
                                </button>
                                </div>
                            ) : (
                                <button onClick={async () => await generateSubSchema(field.name, path)} className="bg-blue-500 hover:bg-blue-700 text-white font-light text-xs py-2 px-4 mt-2 rounded-full shadow-md">
                                    {`Setup ${field.name}`}
                                </button>
                            )
                            }
                            {Array.isArray(field.value) && field.value.length > 0 && (
                                <div className="flex-grow">
                     <div className="mt-4">
                                        <div className="flex flex-col">
                                            <div className="-m-1.5 scrollbar-thin">
                                                <div className="p-1.5 inline-block align-middle">
                                                    <div className="border rounded-lg shadow">
                                                        <table className="divide-y divide-slate-200 table-fixed">
                                                            <thead className="bg-slate-200">
                                                                <tr>
                                                                    {field.schema && field.value[0].item.slice(0, 4).map((subItem: any, subIndex: number) => (
                                                                        <th className="px-3 py-3 text-left text-sm font-medium text-slate-500 uppercase w-1/4" key={subIndex}>{subItem['name']}</th>
                                                                    ))}
                                                                    {!field.schema && (
                                                                        <th className="px-3 py-3 text-left text-sm font-medium text-slate-500 uppercase w-1/4">Value</th>
                                                
                                                                    )}
                                                                    <th scope="col" className="px-6 py-3 text-end text-xs font-medium text-slate-500 uppercase">
                                                                        Action
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-slate-200">
                                                                {field.value.map((row: any, rowIndex: any) => (
                                                                    <tr key={rowIndex}>
                                                                        {row.item && row.item.slice(0, 4).map((header: any, index: any) => {
                                                                            if (index === 0) {
                                                                                return (
                                                                                    <td
                                                                                        className='px-3 py-3 whitespace-pre-wrap text-sm font-medium text-slate-800 truncate hover:text-blue-500 cursor-pointer'
                                                                                        key={`${index}${header['order']}`}
                                                                                        onClick={async () => await readItem(field, path, row, rowIndex)}
                                                                                    >
                                                                                        {header['value']}
                                                                                    </td>
                                                                                )
                                                                            } else {
                                                                                return (
                                                                                    <td
                                                                                        className='px-3 py-3 whitespace-pre-wrap text-sm font-medium text-slate-800 truncate max-w-sm'
                                                                                        key={`${index}${header['order']}`}
                                                                                    >
                                                                                        {header['value']}
                                                                                    </td>
                                                                                )
                                                                            }
                                                                        })}
                                                                        {!field.schema && (
                                                                                              <td
                                                                                              className='px-3 py-3 whitespace-pre-wrap text-sm font-medium text-slate-800 truncate'
                                                                                              key={rowIndex}
                                                                                          >
                                                                                              {row}
                                                                                          </td>
                                                                        )}
                        
                                                                    
                                                                        <td className="px-6 py-4 whitespace-wrap text-end text-sm font-medium">
                                                                            <button onClick={(e) => removeListItem(field, path, rowIndex)} type="button" className="inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-transparent text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:pointer-events-none">Delete</button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                        </div>
                    )}
                    {field.type === 'object' && !editFieldMode[field.name] && (
                        <div className="border p-6 rounded-xl flex-grow">
                            {field.schema && field.schema.length > 0 ? (
                                <div>
                                    <div className="flex flex-row justify-between">
                                        <label htmlFor={field.name} className="block text-lg font-medium leading-6 text-slate-900">
                                            {field.description}
                                        </label>
                                        <button onClick={(e) => removeSubSchema(field, path)} className="bg-white hover:bg-red-200 text-slate-400 font-light text-xs py-2 px-2 rounded-xl border">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m6 4.125l2.25 2.25m0 0l2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                                            </svg>
                                        </button>
                                    </div>
                                    <SchemaEdit docId={docId} formFields={field.schema} updateSchema={updateSchema} generateSubSchema={generateSubSchema} path={[...path, field.name]} setNeedsUpdate={setNeedsUpdate} type={field.type} />
                                </div>
                            ) : (
                                <div>
                                    <label htmlFor={field.name} className="block text-sm font-medium leading-6 text-slate-900">
                                        {field.description}
                                    </label>
                                    <button onClick={async () => await generateSubSchema(field.name, path)} className="bg-blue-500 hover:bg-blue-700 text-white font-light text-xs py-2 px-4 mt-2 rounded-full">
                                        {`Create ${field.name}`}
                                    </button>
                                </div>
                            )
                            }
                        </div>
                    )}
                    {field.type === 'boolean' && !editFieldMode[field.name] && (
                        <div className="sm:col-span-4 flex-grow">
                            <label htmlFor={field.name} className="block text-sm font-medium leading-6 text-slate-900">
                                {field.description}
                            </label>
                            <div className="mt-2">
                                <div className="flex h-6 items-center">
                                    <input
                                        id={field.name}
                                        name={field.name}
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-600"
                                        checked={field.value || false}
                                        onChange={(e) => updateSchema(e.target.checked, path, field.name, 'updateFormFieldValue')}
                                        autoComplete='off'
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {!editFieldMode[field.name] && (
                                <div className="flex flex-row">
                                    <div onClick={() => enableEditMode(field, path)} className="mr-2">
                                        <div className="block text-slate-50 group-hover:text-slate-400 hover:text-slate-500 hover:cursor-pointer">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 block">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div onClick={() => deleteField(field, path)} className="">
                                        <div className="block text-slate-50 group-hover:text-red-400 hover:cursor-pointer">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                    )}
                    {editFieldMode[field.name] && (
                        <div className="flex flex-col flex-grow">
                            <div className="bg-white text-slate-400 font-light text-xs rounded-xl flex flex-row items-center">
                                <div className="flex-grow p-6">
                                    <div>
                                        <label htmlFor='name' className="block text-sm font-medium leading-6 text-slate-500">
                                            Field Name
                                        </label>
                                        <p className="text-xs leading-6 text-slate-400"></p>
                                        <div className="mt-1">
                                            <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-slate-300">
                                                <input
                                                    type="text"
                                                    name="name"
                                                    id="name"
                                                    autoComplete='off'
                                                    className="block flex-1 border-0 bg-transparent py-1.5 pl-2 text-slate-900 placeholder:text-slate-400 focus:ring-0 sm:text-sm sm:leading-6"
                                                    placeholder=""
                                                    value={editFieldName || ''}
                                                    onChange={(e) => setEditFieldName(e.target.value)}
                                                    required={true}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <label htmlFor='description' className="block text-sm font-medium leading-6 text-slate-500">
                                            Field Description
                                        </label>
                                        <p className="text-xs leading-6 text-slate-400"></p>
                                        <div className="mt-1">
                                            <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-slate-300">
                                                <input
                                                    type="text"
                                                    name="description"
                                                    id="description"
                                                    autoComplete='off'
                                                    className="block flex-1 border-0 bg-transparent py-1.5 pl-2 text-slate-900 placeholder:text-slate-400 focus:ring-0 sm:text-sm sm:leading-6"
                                                    placeholder=""
                                                    value={editFieldDescription || ''}
                                                    onChange={(e) => setEditFieldDescription(e.target.value)}
                                                    required={true}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <label htmlFor='type' className="block text-sm font-medium leading-6 text-slate-500">
                                            Field Type
                                        </label>
                                        <p className="text-xs leading-6 text-slate-400"></p>
                                        <div className="mt-1">
                                            <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-slate-300">
                                                <select
                                                    name="type"
                                                    id="type"
                                                    autoComplete="type"
                                                    className="block flex-1 border-0 bg-transparent py-1.5 pl-2 pr-8 text-slate-900 placeholder:text-slate-400 focus:ring-0 sm:text-sm sm:leading-6"
                                                    value={editFieldType || ''}
                                                    onChange={(e) => setEditFieldType(e.target.value)}
                                                    required={true}
                                                    defaultValue={editFieldType}
                                                >
                                                    <option className="text-slate-400" value="">Select a type</option>
                                                    <option value="short_text">Short Text</option>
                                                    <option value="textarea">Text Area</option>
                                                    <option value="number">Number</option>
                                                    <option value="array">List</option>
                                                    <option value="object">Document</option>
                                                    <option value="boolean">Checkbox</option>
                                                    <option value="dropdown_single">Dropdown</option>
                                                    <option value="dropdown_multiple">Multiselect</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    {editFieldType === 'dropdown_single' || editFieldType === 'dropdown_multiple' ? (
                                    <div className="mt-4">
                                        <label htmlFor='values' className="block text-sm font-medium leading-6 text-slate-500">
                                        Selectable Values
                                        </label>
                                        <p className="text-xs leading-6 text-slate-400">Add values that users can select from the dropdown or multiselect.</p>
                                        <div className="mt-1">
                                        <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-slate-300">
                                            <input
                                            type="text"
                                            name="values"
                                            id="values"
                                            autoComplete='off'
                                            className="block flex-1 border-0 bg-transparent py-1.5 pl-2 text-slate-900 placeholder:text-slate-400 focus:ring-0 sm:text-sm sm:leading-6"
                                            placeholder="Value 1, Value 2, Value 3"
                                            // Assuming you have a state variable to hold the selectable values
                                            value={editFieldValues || ''}
                                            onChange={(e) => setEditFieldValues(e.target.value)}
                                            required={true}
                                            // This field is not required to be filled since the user might not want to add any values immediately
                                            />
                                        </div>
                                        </div>
                                    </div>
                                    ) : null}
                                    <div className="sm:col-span-4 mt-4">
                                        <label htmlFor="required" className="block text-sm font-medium leading-6 text-slate-500">
                                            Field Required
                                        </label>
                                        <div className="mt-2">
                                            <div className="flex h-6 items-center">
                                                <input
                                                    id="required"
                                                    name="required"
                                                    type="checkbox"
                                                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-600"
                                                    checked={editFieldRequired || false}
                                                    onChange={(e) => setEditFieldRequired(e.target.checked)}
                                                    autoComplete='off'
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex justify-between mt-4">
                                        <button onClick={() => setEditFieldMode((prevValue: any) => ({ ...prevValue, [field.name]: null }))} className="bg-white hover:bg-red-200 text-slate-400 font-light text-xs py-2 px-2 rounded-xl border">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                        <button onClick={() => editField(field, path)} className="bg-white hover:bg-blue-200 text-slate-400 font-light text-xs py-2 px-2 rounded-xl border">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                        </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                    }

                </div>
                </div>
                 )}
            </Draggable>
            ))}
                {provided.placeholder}
            </div>
            )}
                </Droppable>
            </DragDropContext>
            <div>
                <div className="mt-10">
                    {newField ? (
                        <div className="flex flex-col">
                            <div className="bg-white text-slate-400 font-light text-xs rounded-xl border border-dashed flex flex-row items-center">
                                <div className="flex-grow p-6">
                                    <div>
                                        <label htmlFor='name' className="block text-sm font-medium leading-6 text-slate-500">
                                            Field Name
                                        </label>
                                        <p className="text-xs leading-6 text-slate-400"></p>
                                        <div className="mt-1">
                                            <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-slate-300">
                                                <input
                                                    type="text"
                                                    name="name"
                                                    id="name"
                                                    autoComplete='off'
                                                    className="block flex-1 border-0 bg-transparent py-1.5 pl-2 text-slate-900 placeholder:text-slate-400 focus:ring-0 sm:text-sm sm:leading-6"
                                                    placeholder=""
                                                    value={newFieldName || ''}
                                                    onChange={(e) => setNewFieldName(e.target.value)}
                                                    required={true}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <label htmlFor='description' className="block text-sm font-medium leading-6 text-slate-500">
                                            Field Description
                                        </label>
                                        <p className="text-xs leading-6 text-slate-400"></p>
                                        <div className="mt-1">
                                            <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-slate-300">
                                                <input
                                                    type="text"
                                                    name="description"
                                                    id="description"
                                                    autoComplete='off'
                                                    className="block flex-1 border-0 bg-transparent py-1.5 pl-2 text-slate-900 placeholder:text-slate-400 focus:ring-0 sm:text-sm sm:leading-6"
                                                    placeholder=""
                                                    value={newFieldDescription || ''}
                                                    onChange={(e) => setNewFieldDescription(e.target.value)}
                                                    required={true}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <label htmlFor='type' className="block text-sm font-medium leading-6 text-slate-500">
                                            Field Type
                                        </label>
                                        <p className="text-xs leading-6 text-slate-400"></p>
                                        <div className="mt-1">
                                            <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-slate-300">
                                                <select
                                                    name="type"
                                                    id="type"
                                                    autoComplete="type"
                                                    className="block flex-1 border-0 bg-transparent py-1.5 pl-2 pr-8 text-slate-900 placeholder:text-slate-400 focus:ring-0 sm:text-sm sm:leading-6"
                                                    value={newFieldType || ''}
                                                    onChange={(e) => setNewFieldType(e.target.value)}
                                                    required={true}
                                                    defaultValue={""}
                                                >
                                                    <option className="text-slate-400" value="">Select a type</option>
                                                    <option value="short_text">Short Text</option>
                                                    <option value="textarea">Text Area</option>
                                                    <option value="number">Number</option>
                                                    <option value="array">List</option>
                                                    <option value="object">Document</option>
                                                    <option value="boolean">Checkbox</option>
                                                    <option value="dropdown_single">Dropdown</option>
                                                    <option value="dropdown_multiple">Multiselect</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    {newFieldType === 'dropdown_single' || newFieldType === 'dropdown_multiple' ? (
                                    <div className="mt-4">
                                        <label htmlFor='values' className="block text-sm font-medium leading-6 text-slate-500">
                                        Selectable Values
                                        </label>
                                        <p className="text-xs leading-6 text-slate-400">Add values that users can select from the dropdown or multiselect.</p>
                                        <div className="mt-1">
                                        <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-slate-300">
                                            <input
                                            type="text"
                                            name="values"
                                            id="values"
                                            autoComplete='off'
                                            className="block flex-1 border-0 bg-transparent py-1.5 pl-2 text-slate-900 placeholder:text-slate-400 focus:ring-0 sm:text-sm sm:leading-6"
                                            placeholder="Value 1, Value 2, Value 3"
                                            // Assuming you have a state variable to hold the selectable values
                                            value={newFieldValues || ''}
                                            onChange={(e) => setNewFieldValues(e.target.value)}
                                            // This field is not required to be filled since the user might not want to add any values immediately
                                            />
                                        </div>
                                        </div>
                                    </div>
                                    ) : null}
                                    <div className="sm:col-span-4 mt-4">
                                        <label htmlFor="required" className="block text-sm font-medium leading-6 text-slate-500">
                                            Field Required
                                        </label>
                                        <div className="mt-2">
                                            <div className="flex h-6 items-center">
                                                <input
                                                    id="required"
                                                    name="required"
                                                    type="checkbox"
                                                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-600"
                                                    checked={newFieldRequired || false}
                                                    onChange={(e) => setNewFieldRequired(e.target.checked)}
                                                    autoComplete='off'
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex justify-between mt-4">
                                        <button onClick={() => setNewField(false)} className="bg-white hover:bg-red-200 text-slate-400 font-light text-xs py-2 px-2 rounded-xl border">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                        <button onClick={() => addNewField(path)} className="bg-white hover:bg-blue-200 text-slate-400 font-light text-xs py-2 px-2 rounded-xl border">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                        </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <button onClick={() => setNewField(true)} className="bg-white hover:bg-blue-50 text-slate-400 font-light text-xs py-2 px-2 rounded-xl border border-dashed flex flex-row items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
                            </svg>
                            <div className="text-sm">
                                Add New Field
                            </div>
                        </button>
                    )}
                </div>
                
            </div>
        </div>
    );
};

export default SchemaEdit;
